import { create } from "zustand";
import { Track } from "../types";

export type LoopMode = "off" | "one" | "all";
type PlayerMode = "mini" | "expanded" | "windowed" | "fullscreen";

interface PlayerState {
  isPlaying: boolean;
  currentTrack: Track | null;
  currentTime: number;
  queue: Track[];
  volume: number;
  progress: number;
  mode: PlayerMode;
  loopMode: LoopMode;
  windowedPosition: { x: number; y: number };
  windowedSize: { width: number; height: number };
  visualizerAnalyserNode: AnalyserNode | null; // Step 1.1: Add state property

  setIsPlaying: (playing: boolean) => void;
  setCurrentTrack: (track: Track) => void;
  setCurrentTime: (time: number) => void;
  setQueue: (queue: Track[]) => void;
  setVolume: (volume: number) => void;
  setProgress: (progress: number) => void;
  setMode: (mode: PlayerMode) => void;
  setLoopMode: (mode: LoopMode) => void;
  setWindowedPosition: (position: { x: number; y: number }) => void;
  setWindowedSize: (size: { width: number; height: number }) => void;
  setVisualizerAnalyserNode: (node: AnalyserNode | null) => void; // Step 1.2: Add action type
  togglePlay: () => void;
  seek: (time: number) => void;
  playNext: () => void;
  playPrevious: () => void;
}

export const usePlayerStore = create<PlayerState>((set, get) => ({
  isPlaying: false,
  currentTrack: null,
  currentTime: 0,
  queue: [],
  volume: 0.8, // Default safe volume, will be overridden by saved settings
  progress: 0,
  mode: "mini",
  loopMode: "off",
  windowedPosition: { x: 100, y: 100 },
  windowedSize: { width: 400, height: 600 },
  visualizerAnalyserNode: null, // Step 1.1: Initialize state property

  setIsPlaying: (playing) => set({ isPlaying: playing }),

  setCurrentTrack: (track) => {
    set({
      currentTrack: track,
      currentTime: 0,
      progress: 0,
      // Don't automatically set isPlaying - let useAudioPlayer handle playback state
    });
  },

  setQueue: (queue) => set({ queue }),

  setVolume: (volume) => {
    set({ volume });
  },

  setCurrentTime: (time) => set({ currentTime: time }),

  setProgress: (progress) => set({ progress }),

  setMode: (mode) => set({ mode }),

  setLoopMode: (mode) => set({ loopMode: mode }),

  setWindowedPosition: (position) => set({ windowedPosition: position }),

  setWindowedSize: (size) => set({ windowedSize: size }),

  setVisualizerAnalyserNode: (node) => set({ visualizerAnalyserNode: node }), // Step 1.2: Implement action

  togglePlay: () => set((state) => ({ isPlaying: !state.isPlaying })),

  seek: (time) => set({ currentTime: time }),

  playNext: () => {
    const { queue, currentTrack, loopMode } = get();
    if (!currentTrack) return;

    // Handle single track loop
    if (loopMode === "one") {
      set({
        currentTime: 0,
        progress: 0,
        isPlaying: true,
      });
      return;
    }

    const currentIndex = queue.findIndex((track) => track.id === currentTrack.id);
    if (currentIndex === -1) return;

    // Handle end of queue with loop all
    let nextTrack: Track | undefined;
    if (currentIndex === queue.length - 1) {
      nextTrack = loopMode === "all" ? queue[0] : undefined;
    } else {
      nextTrack = queue[currentIndex + 1];
    }

    if (nextTrack) {
      set({
        currentTrack: nextTrack,
        currentTime: 0,
        progress: 0,
        isPlaying: true,
      });
    } else {
      // No next track and not looping, do nothing
    }
  },

  playPrevious: () => {
    const { queue, currentTrack, currentTime } = get();
    if (!currentTrack) return;

    // If we're more than 5 seconds into the song or it's the first track, restart current track
    const currentIndex = queue.findIndex((track) => track.id === currentTrack.id);
    if (currentTime >= 3 || currentIndex <= 0) {
      set({ currentTime: 0, progress: 0 });
      return;
    }

    // Play previous track
    const prevTrack = queue[currentIndex - 1];
    set({
      currentTrack: prevTrack,
      currentTime: 0,
      progress: 0,
      isPlaying: true,
    });
  },
}));
